<template>
	<div
		class="mini-cart h-full flex flex-col"
		aria-labelledby="mini-cart__title"
		:tabindex="cartIsOpen ? '1' : '-1'"
		:inert="!cartIsOpen"
		:class="[cartIsBlocked ? 'cartIsBlocked' : 'cartIsUnBlocked']"
	>
		<!-- ** HEADER ** -->
		<header class="mini-cart__header px-5 border-b">
			<div class="mini-cart__header-inner flex justify-between items-center">

				<a
					id="mini-cart__title"
					class="mini-cart__title text-xs"
					:href="cartUrl"
				>
					{{ cartName }} <span v-if="data && data.items?.length">({{ cartItems }})</span>
				</a>

				<button
					class="js-mini-cart-close h-12 w-12 flex items-center justify-center"
					@click="closeMiniCart"
					type="button"
					:aria-label="`${closeActionText} ${cartName.toLowerCase()}`"
				>
					<IconClose aria-hidden="true" />
				</button>

			</div>
			<template v-if="data && data.items?.length">
				<div
					class="mini-cart__dtfs-wrap pb-4"
					v-if="freeShippingEnabled"
				>
					<label
						for="mini-cart__dtfs"
						class="block text-center leading-[1.2] text-[0.688rem] font-medium mb-[0.438rem]"
					>
						{{ freeShippingLabel }}
					</label>
					<progress
						id="mini-cart__dtfs"
						class="w-full mini-cart__dtfs"
						:value="freeShippingPercentValue"
						max="100"
					>
					</progress>
				</div>
			</template>

		</header>
		<section
			v-if="initialLoading"
			class="mini-cart__merge-section"
		>
			<div class="text-center">
				<div class="inline-block"></div>
				<p class="text-sm font-bold">{{ cartLoadingState }}</p>
			</div>
		</section>

		<!-- ** CART ITEMS LOOP ** -->
		<div
			v-else
			class="mini-cart__merge-section"
			ref="minicartMergeSection"
		>
			<section
				v-if="data && data.items?.length"
				class="mini-cart__items flex flex-wrap"
			>
				<!-- Regular Items -->
				<cart-item
					v-for="item in regularItems"
					:key="item.id"
					:item="item"
					:updateCartValues="updateCartValues"
					:currency-symbol="currencySymbol"
					:refreshRecommendedProducts="initUpsellProducts"
					:quantity-rule="quantityRuleMap[item.id]"
					@sample-removed="handleSampleRemoved"
				/>

				<!-- Free Sample Items -->
				<div 
					v-if="freeSampleItems.length > 0"
					class="mini-cart__free-samples w-full flex flex-wrap justify-center bg-black/5 relative"
				>
				<div class="absolute inset-0 pl-4 pt-2 z-1 w-fit h-fit">{{ freeSamplesHeading || 'Free Samples' }}:</div> 
					<cart-item
						v-for="item in freeSampleItems"
						:key="item.id"
						:item="item"
						:updateCartValues="updateCartValues"
						:currency-symbol="currencySymbol"
						:refreshRecommendedProducts="initUpsellProducts"
						:quantity-rule="quantityRuleMap[item.id]"
						@sample-removed="handleSampleRemoved"
						class="mini-cart__item--free-sample"
					/>
				</div>
			</section>

			<section
				v-else
				class="mini-cart__is-empty flex items-center flex-col"
			>

				<div class="mini-cart__is-empty-inner  p-6">
					<h2 class="">{{ cartEmptyMessage }}</h2>
				</div>
				<button
					@click.passive="closeMiniCart"
					class="button mini-cart__empty-button"
				>
					{{ cartEmptyButtonText ? cartEmptyButtonText : 'Return to shop' }}
				</button>
				<div
				v-if="showEmptyCartProducts && productsToDisplay.length"
				class="mini-cart__recently-wiewed text-center py-8 px-4 md:px-6"
				>
					<h3 class="mb-4">{{ productsHeading }}</h3>
					<div class="grid grid-cols-2 gap-2">
						<div
							v-for="product in productsToDisplay"
							:key="product.id"
							class="text-center text-xs"
						>
							<a
								:href="'/products/' + product.handle"
								class="flex flex-col"
							>
								<img
									:src="getShopifyImageUrl(product.featured_image, 400)"
									:srcset="`
										${getShopifyImageUrl(product.featured_image, 200)} 200w,
										${getShopifyImageUrl(product.featured_image, 400)} 400w,
										${getShopifyImageUrl(product.featured_image, 600)} 600w,
										${getShopifyImageUrl(product.featured_image, 800)} 800w
									`"
									sizes="(max-width: 640px) 50vw, 25vw"
									:alt="product.title"
									class="object-cover mb-1 w-full mini-cart__recently-wiewed-img"
									loading="lazy"
									width="136"
									height="136"
								/>
								<h4 class="">{{ product.title }}</h4>
								<div class="">
									<span>
										{{ currencySymbol }}{{ Number(product.price).toLocaleString() }}&nbsp;
									</span>
									<span
										v-if="product.compare_at_price && product.compare_at_price > product.price"
										class="line-through opacity-70 mr-1"
									>
										{{ currencySymbol }}{{ Number(product.compare_at_price).toLocaleString() }}
									</span>
								</div>
							</a>
						</div>
					</div>
				</div>
				<div
					id="mini-cart__recently-view"
					class="px-4"
				></div>
			</section>

			<section
				v-if="upshellEnabled && data && data.items?.length && upsellProducts.length"
				class="mini-cart__upsell"
			>
			<header class="mini-cart__upsell-header mb-2">
				<h2 class="">
					{{ upsellHeading }}
				</h2>
			</header>
			<div class="relative">
				<Swiper
					ref="upsellSwiper"
					:slides-per-view="upsellSlidesPerView"
					:space-between="10"
					:observer="true"
					:observe-parents="true"
					:threshold="2"
					:modules="modules"
					:navigation="{
						nextEl: '.mini-cart-swiper-button-next',
						prevEl: '.mini-cart-swiper-button-prev',
					}"
					class="mini-cart__upsell-items"
				>
					<SwiperSlide
						v-for="product in displayedUpsellProducts"
						:key="product.id"
					>
					<UpsellItem
						:product="product"
						:selected-variant-ids="selectedVariantIds"
						:sold-out-text="soldOutText"
						:add-to-bag-text="addtoBagText"
						:item-is-adding-text="itemIsAddingText"
						:quantity-label-text="quantityLabelText"
						:is-loading="isLoading"
						:is-any-loading="isAnyLoading"
						@add-upsell-product="addUpsellProductToCart"
					/>
					</SwiperSlide>
				</Swiper>
				<div class="mini-cart-swiper-button-prev swiper-button-prev"></div>
				<div class="mini-cart-swiper-button-next swiper-button-next"></div>
			</div>
				<!-- Show More Button -->
				<button
					v-if="showMoreButton"
					@click="showMore"
				>
					{{ showMoreText }}
				</button>
			</section>
		</div>

		<!-- ** FOOTER ** -->
		<!-- TO DO: Breakout to separate component -->
		<footer
			v-if="!initialLoading && data && data.items?.length"
			class="mini-cart__footer flex flex-col border-t"
		>

			<div class="mini-cart__footer-utils">
				<!-- **
				FREESAMPLE COMPONENT
				PASSING IN AS PROPS CARTTOTAL, CURRENCYSYMBOL AND FUNCTION TO CHECK CART UPDATE.
			** -->
				<FreeSample
					v-if="freeShampleEnabled"
					ref="freeSampleComponent"
					:cartTotal="totalPrice"
					:cartCount="cartCount"
					:currencySymbol="currencySymbol"
					:removeItem="removeItem()"
					@cart-updated="handleCartUpdated"
				/>

				<!-- GWP COMPONENT - Add this right after FreeSample -->
				<GWPComponent
					v-if="gwpEnabled"
					ref="gwpComponent"
					:cartTotal="totalPrice"
					:cartCount="cartCount"
					:currencySymbol="currencySymbol"
					@cart-updated="handleCartUpdated"
				/>
				<!-- ** ORDER NOTE ** -->
				<OrderNote
					:cart-data="data"
					@cart-updated="handleCartUpdated"
					ref="orderNoteRef"
				/>

				<!-- ** DISCOUNT CODE ** -->
				<DiscountCode
					:cart-data="data"
					@cart-updated="handleCartUpdated"
					ref="discountCodeRef"
				/>

				<!-- ** CART ADD-ON (AUTO-ADD PRODUCT) ** -->
				<CartAddon
					v-if="cartAddonEnabled"
					ref="cartAddonRef"
					:cart-data="data"
					:has-regular-items="hasRegularCartItems"
					@cart-updated="handleCartUpdated"
				/>

				<!-- ** ESTIMATE SHIPPING ** -->
				<CartFetchShippingRates />
			</div>

			<div class="mini-cart__footer-totals">

				<!-- ** TOTALS  ** -->
				<MinicartTotals :data="data" />

				<!-- ** EMPTY CART + CHECKOUT LINKS ** -->
				<div class="text-center py-4">
					<div class="mini-checkout-footer js-mini-cart-footer">
						<a
							:href="checkoutUrl"
							@click="handleCheckout"
							class="mini-cart__button mini-cart__checkout-button button w-full"
							:aria-label="`Links to ${checkoutButtonText}`"
						>
							{{ checkoutButtonText }}
							<span
								v-if="showPriceCheckoutBtn"
								data-affirm-checkout-total
							>
								- {{ formatMoney(data?.total_price) }}
							</span>
						</a>
					</div>

					<button
						v-if="cartAddonOptOutText"
						type="button"
						@click="handleCartAddonOptOut"
						class="underlined-link--always-underlined mt-4 text-xs cursor-pointer"
					>
						{{ cartAddonOptOutText }}
					</button>

					<button
						v-if="showEmptyCartBtn"
						@click="emptyMiniCart"
						class="underlined-link--always-underlined mt-4 text-xs cursor-pointer"
					>
						{{ emptyCartActionText }}
					</button>
				</div>
			</div>

		</footer>
	</div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch, computed, nextTick, provide } from "vue";
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation } from 'swiper/modules';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import CartFetchShippingRates from "./min-cart--fetch-shipping-rates.vue";
import IconClose from "./mini-cart--icon-close.vue";
import CartItem from "./min-cart--cart-item.vue";
import FreeSample from "./min-cart--free-sample.vue";
import MinicartTotals from './mini-cart--totals.vue';
import OrderNote from './min-cart--order-note.vue';
import DiscountCode from './min-cart--discount-code.vue';
import CartAddon from './mini-cart--cart-addon.vue';
import UpsellItem from './min-cart--upsell-item.vue';
import formatMoney from '../helpers/format-money.js';
import { useCartLoading } from './composables/useCartLoading';
import { closeAllAccordions, ACCORDION_SCOPE_KEY, createAccordionScope } from './composables/useAccordion';
import GWPComponent from './min-cart--gwp.vue';


const { setLoading, clearLoading, isLoading, isAnyLoading } = useCartLoading();
const quantityRuleMap = ref({});
const modules = [Navigation]
const accordionScopeId = createAccordionScope();

provide(ACCORDION_SCOPE_KEY, accordionScopeId);

/**
 * Generate Shopify image URL with specific width
 * @param {string} imageUrl - Original Shopify image URL
 * @param {number} width - Desired width
 * @returns {string} Image URL with width parameter
 */
const getShopifyImageUrl = (imageUrl, width) => {
	if (!imageUrl) return '';
	
	// If URL already has parameters, append width
	if (imageUrl.includes('?')) {
		return `${imageUrl}&width=${width}`;
	}
	
	// Otherwise add width parameter
	return `${imageUrl}?width=${width}`;
};

/* Define vars */
const freeSamplesHeading = ref('');
const closeActionText = window.miniCartCustomSettings.closeActionText || "Close";
const cartName = window.miniCartCustomSettings.language.cartName || "Cart";
const cartUrl = computed(() => `${window.Shopify.routes.root}cart`);
const checkoutUrl = computed(() => `${window.Shopify.routes.root}checkout`);
const checkoutButtonText = window.miniCartCustomSettings.language.checkoutButton || "CONTINUE TO CHECKOUT";
const upsellHeading = window.miniCartCustomSettings.upsellHeading || "Purchased frequently with";
const recentlyViewedHeading = window.miniCartCustomSettings.recentlyViewedHeading || "Recently Viewed Products";
const itemIsAddingText = window.miniCartCustomSettings.itemIsAddingText || "Adding...";
const freeShippingThreshold = ref(window.miniCartCustomSettings.freeShippingThreshold);
const cartEmptyButtonText = ref(window.miniCartCustomSettings.cartEmptyButtonText);
const cartEmptyMessage = ref(window.miniCartCustomSettings.cartEmptyMessage);
const cartLoadingState = ref(window.miniCartCustomSettings.cartLoadingState);
const currencySymbol = ref(window.miniCartCustomSettings.currencySymbol);
const numberOfRecommendedProducts = ref(window.miniCartCustomSettings.numberOfRecommendedProducts);
const upsellSlidesPerView = ref(window.miniCartCustomSettings.upsellSlidesPerView || 2.5);
const showEmptyCartBtn = ref(window.miniCartCustomSettings.showEmptyCart);
const showEmptyCartProducts = ref(window.miniCartCustomSettings.showEmptyCartProducts ?? true);
const soldOutText = ref(window.miniCartCustomSettings.soldOutText);
const addtoBagText = ref(window.miniCartCustomSettings.addtoBagText);
const showMoreText = ref(window.miniCartCustomSettings.showMoreText);
const emptyCartActionText = ref(window.miniCartCustomSettings.emptyCartActionText);
const quantityLabelText = ref(window.miniCartCustomSettings.quantityLabelText);
const showPriceCheckoutBtn = ref(window.miniCartCustomSettings.showPriceCheckoutBtn);
const freeShippingEnabled = computed(() => window.miniCartCustomSettings.freeShippingEnabled);
const freeShampleEnabled = computed(() => window.miniCartCustomSettings.freeShampleEnabled);
const upshellEnabled = computed(() => window.miniCartCustomSettings.upshellEnabled);
const freeShippingPercentValue = ref(0);
const cartIsOpen = ref(false);
const cartIsBlocked = ref(true);
const loading = ref(true);
const initialLoading = ref(true);
const data = ref([]);
const cartTotal = ref(0);
const cartCount = ref(0);
const subtotalTitle = ref(window.miniCartCustomSettings.subTotalText);
const totalPrice = ref(0);
const subtotalPrice = ref(0);
const totalSaved = ref(0);
const discounts = ref([]);
const upsellProducts = ref([]);
const selectedVariantIds = ref({});
const freeSampleComponent = ref(null);
const isFetching = ref(false);
const displayedUpsellProducts = ref([]);
const initialDisplayCount = ref(5);
const additionalDisplayCount = ref(5);
const minicartMergeSection = ref(null);
const upsellSwiper = ref(null);
const gwpEnabled = computed(() => window.miniCartCustomSettings.gwpEnabled);
const cartAddonEnabled = computed(() => window.cartAddonConfig?.enabled && window.cartAddonConfig?.product?.id);
const cartAddonOptOutText = computed(() => {
	if (!cartAddonEnabled.value || !hasRegularCartItems.value) return '';

	return window.cartAddonConfig?.optOutText || '';
});
const gwpComponent = ref(null);
const cartAddonRef = ref(null);
const customHeading = window.miniCartCustomSettings.customHeading || "You may also like";
const fallbackProducts = ref([]);
const customProductList = ref(window.miniCartCustomSettings.customProductList);
//Expose Vue's variables to the Window for global useage.
window.cartTotal = cartTotal.value || 0;
window.cartCount = cartCount.value || 0;
const recentlyViewedProducts = ref([]);

// Helper function to check if item is a cart add-on
const isCartAddonItem = (item) => {
  return item.properties?._cart_addon === true ||
         item.properties?._cart_addon === 'true';
};

// Helper function to check if item is a free sample
const isFreeSampleItem = (item) => {
  // Only items with _sample property are free samples
  return item.properties?._sample === true || 
         item.properties?._sample === 'true';
};

// Computed properties to separate regular items and free samples
const regularItems = computed(() => {
	return data.value.items?.filter(item => !isFreeSampleItem(item) && !isCartAddonItem(item)) || [];
});

const hasRegularCartItems = computed(() => regularItems.value.length > 0);

const freeSampleItems = computed(() => {
	return data.value.items?.filter(item => isFreeSampleItem(item)) || [];
});

const productsToDisplay = computed(() => {
    return recentlyViewedProducts.value.length > 0 
        ? recentlyViewedProducts.value 
        : fallbackProducts.value;
});

const productsHeading = computed(() => {
    return recentlyViewedProducts.value.length > 0 
        ? recentlyViewedHeading 
        : customHeading;
});
async function fetchRecentlyViewedProducts() {
	const viewedProducts = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
	if (!viewedProducts.length) {
		recentlyViewedProducts.value = [];
		return;
	}

	const fetched = await Promise.all(
		viewedProducts.map(async (productId) => {
			try {
				// Step 1: Get handle via predictive search
				const res = await fetch(`/search/suggest.json?q=id:${productId}&resources[type]=product`);
				const data = await res.json();
				const found = data.resources.results.products[0];
				if (!found) return null;

				// Step 2: Fetch full product object by handle
				const full = await fetch(`/products/${found.handle}.js`);
				const product = await full.json();

				return {
					id: product.id,
					title: product.title,
					handle: product.handle,
					featured_image: product.featured_image,
					price: product.price_max / 100,
					compare_at_price: product.compare_at_price_max ? product.compare_at_price_max / 100 : null,
					variants: product.variants,
					vendor: product.vendor,
				};
			} catch (err) {
				console.error('Failed to fetch product', err);
				return null;
			}
		})
	);

	recentlyViewedProducts.value = fetched.filter(Boolean);
}
async function fetchCustomProducts() {
    if (!customProductList.value || customProductList.value.length === 0) {
        fallbackProducts.value = [];
        return;
    }

    const fetched = await Promise.all(
        customProductList.value.map(async (productHandle) => {
            try {
                const full = await fetch(`/products/${productHandle}.js`);
                const product = await full.json();

                return {
                    id: product.id,
                    title: product.title,
                    handle: product.handle,
                    featured_image: product.featured_image,
                    price: product.price_max / 100,
                    compare_at_price: product.compare_at_price_max ? product.compare_at_price_max / 100 : null,
                    variants: product.variants,
                    vendor: product.vendor,
                };
            } catch (err) {
                console.error('Failed to fetch product', err);
                return null;
            }
        })
    );

    fallbackProducts.value = fetched.filter(Boolean);
}

const fetchQuantityRulesForCart = async (items) => {
  for (const item of items) {
    // Skip if already cached
    if (quantityRuleMap.value[item.id]) continue;

    try {
      const res = await fetch(`/products/${item.handle}.js`);
      const product = await res.json();

      // Find the correct variant
      const variant = product.variants.find(v => v.id === item.variant_id);

      quantityRuleMap.value[item.id] = {
        min: variant?.quantity_rule?.min ?? 1,
        max: variant?.quantity_rule?.max ?? null,
        increment: variant?.quantity_rule?.increment ?? 1,
      };
    } catch (err) {
      console.warn('Failed to fetch quantity rules for', item.product_title);
      quantityRuleMap.value[item.id] = { min: 1, max: null, increment: 1 };
    }
  }
};


onMounted(fetchRecentlyViewedProducts);
onMounted(fetchCustomProducts);


const cartItems = computed(() => {
	if (!data.value.items?.length) return 0;

	return data.value.items
		.filter(item => !isFreeSampleItem(item) && !isCartAddonItem(item))
		.reduce((total, item) => total + item.quantity, 0);
})

const freeShippingLabel = computed(() => {
	const thresholdInCurrency = freeShippingThreshold.value * 100;
	const freeShipping = freeShippingThreshold.value * 100 - data.value.total_price
	return data.value.total_price >= thresholdInCurrency ? "Free shipping activated!" : `You’re ${window.miniCartCustomSettings.currencySymbol}${freeShipping.toFixed(2) / 100} away from FREE Shipping`;
});

// Order Note component ref
const orderNoteRef = ref(null);

// Discount Code component ref
const discountCodeRef = ref(null);


// Debounce function logic added so not making multiple requests to update cart
const debounce = (func, wait) => {
	let timeout;
	return function executedFunction(...args) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
};

// General Cart Methods
// Handle checkout click
function handleCheckout(e) {
	if (orderNoteRef.value && orderNoteRef.value.orderNote) {
		orderNoteRef.value.updateCart(e, orderNoteRef.value.orderNote);
	}
}

async function handleCartAddonOptOut() {
	if (cartAddonRef.value?.optOutAndCheckout) {
		await cartAddonRef.value.optOutAndCheckout();
	}
}




const freeShippingPercent = (cartTotal) => {
	const threshold = parseInt(freeShippingThreshold.value) * 100; // Converted to cents
	const totalPriceWithTaxes = parseFloat(cartTotal);
	const maxCartTotal = threshold; // Now the progress bar will reach 100% at the actual free shipping threshold
	const percent =
		(totalPriceWithTaxes * 100) / maxCartTotal > 100
			? 100
			: (totalPriceWithTaxes * 100) / maxCartTotal;
	freeShippingPercentValue.value = parseInt(percent);

	// document
	// 	.getElementById("mini-cart__dtfs")
	// 	.style.width = `${freeShippingPercentValue.value}%`;

	return freeShippingPercentValue.value;
};


const updateCartValues = async (response) => {
	cartIsBlocked.value = false;
	loading.value = false;
	data.value = response;
	cartTotal.value = data.value.total_price;
	cartCount.value = data.value.item_count;
	freeShippingPercent(data.value.total_price);

	// Update cart items directly from response
	data.value.items = response.items;
	// Move the updates to these values here.
	totalPrice.value = response.total_price;
	subtotalPrice.value = response.original_total_price; // Replace 'original_total_price' with the key for the original price in your data
	totalSaved.value = subtotalPrice.value - totalPrice.value;
	if (Array.isArray(response.cart_level_discount_applications)) {
		discounts.value = response.cart_level_discount_applications.map(discount => ({
			title: discount.title,
			total_allocated_amount: discount.total_allocated_amount,
		}));
	}

	// Update cart total elems that are outside of Vue.
	// Update cart total elements (both item count and total price)

	if (response.total_price == 0 && response.item_count == 0) {
		emptyMiniCart();
	}
};

const getCart = async () => {

	if (isFetching.value) return;

	isFetching.value = true;
	cartIsBlocked.value = true;

	await fetch(Shopify.routes.root + "cart.js")
		.then((r) => r.json())
		.then((response) => {
			if (document.querySelector('.js-mini-cart-footer')) {
				if (response.items && response.items.length > 0) {
					document.querySelector('.js-mini-cart-footer').classList.remove('!hidden')
				} else {
					document.querySelector('.js-mini-cart-footer').classList.add('!hidden')
				}
			}

			// Here we are checking if the cart has items
			if (response.items && response.items.length > 0) {
				// Update cart values if there are items
				updateCartValues(response);
				fetchQuantityRulesForCart(response.items).then(() => {
					initUpsellProducts().catch(error => {
						console.error('Error initializing upsell products:', error);
					});
				});
			} else {
				console.log("Cart is empty.");
			}
			document.dispatchEvent(new CustomEvent("cart:updated", { detail: response }));
		}
		)
		.finally(() => {
			isFetching.value = false;
			initialLoading.value = false;
		});

	const cartTotalCounters = document.querySelectorAll('.js-cart-total');

	cartTotalCounters.forEach(function (element) {
		// Get the total price and item count from the data
		const totalPrice = data.value.total_price;
		const itemCount = data.value.item_count;

		// If the total price is 0, hide the element
		if (itemCount == 0 || itemCount == undefined) {
			element.style.display = 'none';
		} else {
			element.style.display = 'flex';
			// Otherwise, update both item count and total price using appropriate properties from data
			const formattedPrice = (totalPrice / 100).toLocaleString('en-US', {
				style: 'currency',
				currency: 'USD',
			});
			element.innerHTML = `
      <span class="cart-total__count">${itemCount}</span>
    `;
		}
	});
};


// HANDEL THE ADD TO CART FOR THE UPSELL PRODUCTS
const addUpsellProductToCart = async (event, product) => {
	if (isAnyLoading()) {
		console.log("Another operation is in progress, skipping add to cart");
		return;
	}

	console.log("Adding upsell product to cart:", product.title, product.id);
	setLoading(product.id);

	const formData = new FormData(event.target);

	// Log form data for debugging
	console.log("Form data:", Object.fromEntries(formData.entries()));

	try {
		const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
			method: 'POST',
			body: formData
		});

		if (!response.ok) {
			const errorText = await response.text();
			throw new Error(`Failed to add to cart (${response.status}): ${errorText}`);
		}

		const result = await response.json();
		console.log("Successfully added to cart:", result);

		if (minicartMergeSection.value) {
			minicartMergeSection.value.scrollTo({ top: 0, behavior: 'smooth' });
		}

		if (document.querySelector('.page-cart')) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}

		await getCart();
		cartIsBlocked.value = false;
	} catch (error) {
		console.error("Error adding upsell product to cart:", error);
		console.error("Product details:", {
			id: product.id,
			title: product.title,
			variants: product.variants,
			selectedVariant: selectedVariantIds.value[product.id]
		});
		cartIsBlocked.value = false;
	} finally {
		clearLoading();
	}
};

const handleCartUpdated = (updatedCart) => {
	if (isFetching.value) return;

	data.value = updatedCart;
	getCart();
};


const handleSampleRemoved = (removedItem) => {
  console.log("Handling sample removed for:", removedItem);
  
  // Handle Free Sample removal
  if (freeSampleComponent.value) {
    freeSampleComponent.value.notifySampleRemoved(removedItem.product_id || removedItem.id);
  }
  
  // Handle GWP removal
  if (gwpComponent.value) {
    gwpComponent.value.notifyGWPRemoved(removedItem.product_id || removedItem.id);
  }
};


const removeItem = (variantId) => {
	cartIsBlocked.value = true;

	// find the line item with the variant id
	const lineItem = data.value.items.find(
		(item) => item.variant_id === variantId
	);

	if (lineItem) {
		fetch(`/cart/change.js`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-Requested-With": "XMLHttpRequest",
			},
			body: JSON.stringify({
				id: lineItem.key,
				quantity: 0, // set the quantity to 0 to remove it from the cart
			}),
		})
			.then((r) => r.json())
			.then((response) => {
				// update the cart
				data.value = response;
				freeShippingPercent(data.value.total_price);

				cartIsBlocked.value = false;
			})
			.catch((error) => {
				console.error(error);
				cartIsBlocked.value = false;
			});
	}
};

const removeItems = (idsArray) => {
	alert(idsArray);
};

const updateItem = (id) => {
	alert(id);
};

const updateItems = (idsArray) => {
	alert(idsArray);
};

const convertOneTimeToSubscription = (id) => {
	alert(id);
};

const convertSubscriptionToOneTime = (id) => {
	alert(id);
};

const toggleMiniCart = (direction) => {
	console.log(direction)
	if (direction == "close") {
		cartIsOpen.value = false;
		document.body.classList.remove("mini-cart-open");
		closeAllAccordions(accordionScopeId);
	}
	if (direction == "open") {
		cartIsOpen.value = true;
		document.body.classList.remove("nav-open");
		document.body.classList.add("mini-cart-open");
		const isScrolled = document.body.classList.contains("scrolled");
		if (isScrolled) {
			document.querySelector('.mini-cart-wrap').style.top = "0px";
		} else {
			document.querySelector('.mini-cart-wrap').style.top = "30px";
		}
	}
};

const openMiniCart = () => {
	cartIsOpen.value = true;
	document.body.classList.remove("nav-open");
	document.body.classList.add("mini-cart-open");
};

const closeMiniCart = () => {
	cartIsOpen.value = false;
	document.body.classList.remove("mini-cart-open");
	closeAllAccordions(accordionScopeId);
};

const emptyMiniCart = () => {
	cartIsBlocked.value = true;
	fetch(Shopify.routes.root + "cart/clear.js", { method: "POST" })
		.then((r) => r.json())
		.then((r) => {
			data.value = r;
			freeShippingPercent(data.value.total_price);
			getCart();
			cartIsBlocked.value = false;
			cartCount.value = data.value.item_count;
			cartTotal.value = data.value.total_price;
			const cartTotalCounters = document.querySelectorAll('.js-cart-total');
			cartTotalCounters.forEach(function (element) {
				element.textContent = data.value.item_count;
			});
			document.dispatchEvent(new CustomEvent("miniCart:emptied"));
		})
		.catch((error) => {
			console.error(error);
			cartIsBlocked.value = false;
		});
};

const initUpsellProducts = async () => {
	try {
		if (!data.value?.items?.length) {
			console.log("No cart items found for upsell recommendations");
			return;
		}

		const productIdsInCart = data.value.items.map(item => item.product_id);
		const allRecommendedProducts = [];

		// Process recommendations sequentially to avoid rate limiting
		for (let i = 0; i < productIdsInCart.length; i++) {
			const productId = productIdsInCart[i];

			try {
				const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=${numberOfRecommendedProducts.value}&intent=related`);

				if (!response.ok) {
					console.warn(`Failed to fetch recommendations for product ${productId}: ${response.status}`);
					continue;
				}

				const newProductData = await response.json();

				if (newProductData?.products?.length) {
					allRecommendedProducts.push(...newProductData.products);
				}

				// Add delay between requests to avoid rate limiting
				if (i < productIdsInCart.length - 1) {
					await new Promise(resolve => setTimeout(resolve, 150));
				}
			} catch (error) {
				console.error(`Error fetching recommendations for product ${productId}:`, error);
			}
		}

		if (!allRecommendedProducts.length) {
			console.log("No recommended products found");
			upsellProducts.value = [];
			displayedUpsellProducts.value = [];
			return;
		}

		// LOGIC TO REMOVE DUPLICATE PRODUCTS
		const uniqueRecommendedProducts = allRecommendedProducts.filter((product, index, self) =>
			index === self.findIndex((p) => p.id === product.id)
		);

		// CHECK TO MAKE SURE THE PRODUCT IS NOT ALREADY IN THE CART
		const filteredRecommendedProducts = uniqueRecommendedProducts.filter(product =>
			!productIdsInCart.includes(product.id)
		);

		upsellProducts.value = filteredRecommendedProducts;

		// Set up variant selection for each product
		upsellProducts.value.forEach(product => {
			if (product.variants?.length > 0) {
				const availableVariant = product.variants.find(variant => variant.available);
				if (availableVariant) {
					selectedVariantIds.value[product.id] = availableVariant.id;
				} else {
					console.warn("No available variant for product:", product.title, product.id);
				}
			}
		});

		await updateDisplayedProducts();
		console.log(`Successfully loaded ${filteredRecommendedProducts.length} upsell products`);

	} catch (error) {
		console.error('Error in initUpsellProducts:', error);
		upsellProducts.value = [];
		displayedUpsellProducts.value = [];
	}
}

const updateDisplayedProducts = async () => {
	displayedUpsellProducts.value = upsellProducts.value.slice(0, initialDisplayCount.value);

	// Wait for DOM to update, then refresh Swiper
	await nextTick();

	// Give a small delay to ensure Swiper is fully rendered
	setTimeout(() => {
		if (upsellSwiper.value && upsellSwiper.value.swiper) {
			const swiperInstance = upsellSwiper.value.swiper;
			swiperInstance.update();
		}
	}, 100);
};

const showMoreButton = computed(() => {
	return (
		initialDisplayCount.value < upsellProducts.value.length &&
		initialDisplayCount.value + additionalDisplayCount.value <= upsellProducts.value.length
	);
});

const showMore = async () => {
	const currentCount = displayedUpsellProducts.value.length;
	const newCount = Math.min(
		currentCount + additionalDisplayCount.value,
		upsellProducts.value.length
	);
	const newItems = upsellProducts.value.slice(currentCount, newCount);
	displayedUpsellProducts.value = [
		...newItems,
		...displayedUpsellProducts.value
	];
	initialDisplayCount.value = newCount;
	await nextTick();
	setTimeout(() => {
		if (upsellSwiper.value && upsellSwiper.value.swiper) {
			const swiperInstance = upsellSwiper.value.swiper;
			swiperInstance.update();
		}
	}, 100);
};

const checkCart = () => {
	if (document.querySelector('.page-cart')) {
		cartIsOpen.value = true;
	}
};


onMounted(() => {

	getCart();
	checkCart();
	if (window.freeSamples?.heading) {
    freeSamplesHeading.value = window.freeSamples.heading;
  }
	
	loading.value = false;
	cartIsBlocked.value = false;

	// Add escape key listener to close mini-cart
	const handleEscapeKey = (event) => {
		if (event.key === 'Escape' && cartIsOpen.value) {
			closeMiniCart();
		}
	};

	document.addEventListener('keydown', handleEscapeKey);

	// Cleanup event listener when component unmounts
	onUnmounted(() => {
		document.removeEventListener('keydown', handleEscapeKey);
	});

	/* Expose to window if you need to access these functions outside of Vue. */
	window.closeMiniCart = closeMiniCart;
	window.toggleMiniCart = toggleMiniCart;
	window.openMiniCart = openMiniCart;
	window.getCart = getCart;
	window.emptyMiniCart = emptyMiniCart;


});
</script>

<style lang="scss" scoped>
@use '../../css/abstract/buttons';
.button {
	@extend %button;
}
</style>
